import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { MenuItem } from '@/types/menu'
import { getMenuTree } from '@/api/system/menu'
import i18n from '@/locales'
import { features } from '@/config/brand'

/** 递归提取菜单树中所有叶子/节点的 url，用于权限校验 */
export function flattenMenuUrls(items: MenuItem[]): Set<string> {
  const urls = new Set<string>()
  function walk(nodes: MenuItem[]) {
    for (const node of nodes) {
      if (node.path) urls.add(node.path)
      if (node.children?.length) walk(node.children)
    }
  }
  walk(items)
  return urls
}

/**
 * C2C、借贷业务暂时隐藏：按路径前缀过滤掉 OTC（C2C）、Margin（借贷）模块的菜单。
 * 恢复时清空此数组即可。
 */
const HIDDEN_PATH_PREFIXES = [
  '/otc',
  '/margin',
  // velax 定制：非 velax 品牌隐藏 APP 域名管理入口；菜单滤除后 flattenMenuUrls
  // 不含该路径，PermissionGuard 会将直达访问判为 403（见 src/config/brand.ts）。
  ...(features.appDomainMgmt ? [] : ['/system/app-domain']),
  // velax 定制：非 velax 部署隐藏现货订单管理入口（issue #79）。
  ...(features.spotAdmin ? [] : ['/trading/spot-orders']),
  // velax 定制：非 velax 部署隐藏跟单运营视图（跟随关系 / 跟单持仓 / 执行日志）入口。
  ...(features.copyTradeAdminExt
    ? []
    : ['/copy-trade/subscriptions', '/copy-trade/positions', '/copy-trade/execution-logs']),
]

function isHiddenByPrefix(path: string): boolean {
  return HIDDEN_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
}

/** 递归过滤掉 is_visible 为 false 以及被隐藏前缀命中的节点 */
function filterVisible(items: MenuItem[]): MenuItem[] {
  const result: MenuItem[] = []
  for (const item of items) {
    if (item.is_visible === false || isHiddenByPrefix(item.path)) continue
    const hadChildren = !!item.children?.length
    const children = item.children?.length ? filterVisible(item.children) : undefined
    // C2C、借贷业务暂时隐藏：父分组（如 path 为空的「杠杆借贷」）在子菜单被全部隐藏后，自身也不再渲染。
    //
    // 判定必须限定在「子项是被隐藏前缀整组滤掉」这一种情况：若只要子项被过滤光就连父级一起丢，
    // 「本身是页面、只是挂了隐藏子路由」的菜单会整条消失——/user/list 挂着 is_visible=false 的
    // /user/detail，会连同父级「用户管理」一起从侧边栏蒸发，进而被路由守卫判成 403。
    const hiddenByPrefixOnly =
      hadChildren && item.children!.every((child) => isHiddenByPrefix(child.path))
    if (hiddenByPrefixOnly && (!children || children.length === 0)) continue
    result.push({ ...item, children })
  }
  return result
}

interface MenuState {
  items: MenuItem[]
  loading: boolean
  error: string | null
  /** 本次会话是否已从服务端加载完成（不持久化，刷新后重置为 false） */
  initialized: boolean
}

interface MenuActions {
  fetchMenus(): Promise<void>
  setItems(items: MenuItem[]): void
  clearMenus(): void
}

type MenuStore = MenuState & MenuActions

const useMenuStore = create<MenuStore>()(
  persist(
    (set) => ({
      items: [],
      loading: false,
      error: null,
      initialized: false,

      async fetchMenus() {
        // 每次从服务端拉取前清除旧缓存，确保 isVisible 过滤生效
        sessionStorage.removeItem('menu-storage')
        set({ loading: true, error: null })
        try {
          const items = await getMenuTree()
          set({ items: filterVisible(items), loading: false, initialized: true })
        } catch (err: unknown) {
          const message =
            err && typeof err === 'object' && 'message' in err
              ? String((err as { message: unknown }).message)
              : i18n.t('nav.loadFailed')
          set({ error: message, loading: false, initialized: true })
        }
      },

      setItems(items: MenuItem[]) {
        set({ items })
      },

      clearMenus() {
        set({ items: [], error: null, initialized: false })
      },
    }),
    {
      name: 'menu-storage',
      storage: createJSONStorage(() => sessionStorage),
      // initialized 不持久化，每次刷新都重新从服务端加载
      partialize: (state) => ({ items: state.items }),
    }
  )
)

export default useMenuStore
